/**
 * CollectionFilter Web Component
 * Handles collection filtering using Shopify Section Rendering API
 * Auto-initializes when added to DOM, auto-reinitializes when replaced
 */
class CollectionFilter extends HTMLElement {
    constructor() {
        super();

        // Component state
        this.isLoading = false;
        this.currentParams = new URLSearchParams();
        this.debounceDelay = 300;

        // Queue for filter changes to prevent race conditions
        this.pendingChanges = [];

        // Debounced update method
        this.debouncedUpdate = this.debounce(this.updateFilters.bind(this), this.debounceDelay);

        // Debounced search method
        this.debouncedSearch = this.debounce(this.handleFilterSearch.bind(this), 300);
    }

    /**
     * Called automatically when element is added to DOM
     * This handles both initial load AND after HTML replacement!
     */
    connectedCallback() {

        // Initialize component
        this.sectionId = this.dataset.sectionId || 'main-collection';

        // Check if we have preserved params from a replacement
        if (this.dataset.preservedParams) {
            this.currentParams = new URLSearchParams(this.dataset.preservedParams);
            delete this.dataset.preservedParams;
        } else {
            this.currentParams = new URLSearchParams(window.location.search);
        }

        // Check for preserved UI state
        let preservedUIState = null;
        if (this.dataset.filterUIState) {
            preservedUIState = JSON.parse(this.dataset.filterUIState);
            delete this.dataset.filterUIState;
        }

        // Bind events (scoped to this component automatically)
        this.bindEvents();

        // Initialize show more/less functionality and search
        this.initializeFilterUI();

        // Restore UI state if available (after initialization)
        if (preservedUIState) {
            this.restoreFilterUIState(preservedUIState);
        }

        // Restore filter group collapse states
        this.restoreFilterGroupStates();

        // Initialize price range slider (ONCE, after all DOM is ready)
        this.initPriceRangeSlider();

        // Update clear button visibility based on active filters
        this.updateClearButtonVisibility();

        // Set up popstate handler (only once per page)
        if (!window.collectionFilterPopstateHandler) {
            window.addEventListener('popstate', () => {
                const activeFilter = document.querySelector('collection-filter');
                if (activeFilter) {
                    activeFilter.currentParams = new URLSearchParams(window.location.search);
                    activeFilter.updateFilters(false);
                }
            });
            window.collectionFilterPopstateHandler = true;
        }
    }

    /**
     * Update clear button visibility based on active filters
     */
    updateClearButtonVisibility() {
        // Search entire document since buttons might be outside component
        const clearButtons = document.querySelectorAll('[data-show-when-filtered]');
        const hasActiveFilters = this.hasActiveFilters();

        clearButtons.forEach(button => {
            if (hasActiveFilters) {
                button.classList.remove('hidden');
            } else {
                button.classList.add('hidden');
            }
        });
    }

    /**
     * Check if there are any active filters
     */
    hasActiveFilters() {
        for (const key of this.currentParams.keys()) {
            if (key.startsWith('filter.')) {
                return true;
            }
        }
        return false;
    }

    /**
     * Called automatically when element is removed from DOM
     */
    disconnectedCallback() {
        // Clean up body scroll lock if this component is removed while modal was open
        document.body.classList.remove('overflow-hidden', 'modal--open');
        this.showShopifyChat();
    }

    /**
     * Bind events - all automatically scoped to this component!
     */
    bindEvents() {
        // Set up click-outside handler for dropdowns (only once)
        if (!window.filterDropdownClickOutsideHandler) {
            document.addEventListener('click', (e) => {
                this.handleClickOutside(e);
            });
            window.filterDropdownClickOutsideHandler = true;
        }

        // Handle filter changes with precise tracking
        this.addEventListener('change', (e) => {
            if (e.target.matches('[data-filter-input]')) {
                this.handleFilterChange(e.target);
            } else if (e.target.matches('[data-sort-select]')) {
                this.handleSortChange(e);
            }
        });

        // Handle price range changes and search inputs
        this.addEventListener('input', (e) => {
            if (e.target.matches('[data-price-min], [data-price-max]')) {
                this.handlePriceChange(e.target);
            } else if (e.target.matches('[data-filter-search]')) {
                // Debounce search input to avoid too many updates
                this.debouncedSearch(e.target);
            }
        });

        // Handle clear filter buttons and show more/less
        this.addEventListener('click', (e) => {
            if (e.target.matches('[data-remove-filter]') || e.target.closest('[data-remove-filter]')) {
                const target = e.target.matches('[data-remove-filter]') ? e.target : e.target.closest('[data-remove-filter]');
                this.handleRemoveFilter({ target });
            } else if (e.target.matches('[data-clear-all-filters]')) {
                this.handleClearAllFilters(e);
            } else if (e.target.matches('[data-remove-price-filter]') || e.target.matches('[data-price-clear]') || e.target.closest('[data-price-clear]')) {
                this.handleRemovePriceFilter(e);
            } else if (e.target.matches('[data-filter-more-items]') || e.target.closest('[data-filter-more-items]')) {
                const button = e.target.matches('[data-filter-more-items]') ? e.target : e.target.closest('[data-filter-more-items]');
                this.handleShowMoreToggle(button);
            } else if (e.target.matches('[data-search-clear]') || e.target.closest('[data-search-clear]')) {
                const clearButton = e.target.matches('[data-search-clear]') ? e.target : e.target.closest('[data-search-clear]');
                this.handleSearchClear(clearButton);
            } else if (e.target.matches('[data-mobile-filter]') || e.target.closest('[data-mobile-filter]')) {
                // Open mobile filter modal
                const button = e.target.matches('[data-mobile-filter]') ? e.target : e.target.closest('[data-mobile-filter]');
                this.handleMobileFilterOpen(button);
            } else if (e.target.matches('[data-close-mobile-modal]') || e.target.closest('[data-close-mobile-modal]')) {
                // Close mobile modal
                                this.handleMobileModalClose();
            } else if (e.target.matches('[data-show-filter]')) {
                // Show specific filter in mobile
                this.handleShowFilter(e.target);
            } else if (e.target.matches('[data-modal-back]') || e.target.closest('[data-modal-back]')) {
                // Go back in mobile modal
                                this.handleMobileBack();
            } else if (e.target.matches('[data-apply-mobile-filters]')) {
                // Apply mobile filters and close
                this.handleApplyMobileFilters();
            } else if (e.target.matches('[data-clear-current-filter]')) {
                // Clear current filter in mobile modal
                this.handleClearCurrentFilter();
            } else if (e.target.matches('.filter-group__header') || e.target.closest('.filter-group__header')) {
                // Toggle filter group collapse
                const header = e.target.matches('.filter-group__header') ? e.target : e.target.closest('.filter-group__header');
                this.handleFilterGroupToggle(header);
            } else if (e.target.matches('[data-filter-drawer-toggle]') || e.target.closest('[data-filter-drawer-toggle]')) {
                // Toggle drawer
                this.handleDrawerToggle();
            } else if (e.target.matches('[data-filter-drawer-close]') || e.target.closest('[data-filter-drawer-close]')) {
                // Close drawer
                this.handleDrawerClose();
            } else if (e.target.matches('[data-filter-overlay]')) {
                // Close drawer via overlay
                this.handleDrawerClose();
            } else if (e.target.matches('[data-filter-dropdown-toggle]') || e.target.closest('[data-filter-dropdown-toggle]')) {
                // Toggle horizontal dropdown
                const toggle = e.target.matches('[data-filter-dropdown-toggle]') ? e.target : e.target.closest('[data-filter-dropdown-toggle]');
                this.handleDropdownToggle(toggle);
            } else if (e.target.matches('[data-filter-dropdown-close]') || e.target.closest('[data-filter-dropdown-close]')) {
                // Close specific dropdown
                const closeBtn = e.target.matches('[data-filter-dropdown-close]') ? e.target : e.target.closest('[data-filter-dropdown-close]');
                this.handleDropdownClose(closeBtn);
            } else if (e.target.matches('[data-apply-filter-group]') || e.target.closest('[data-apply-filter-group]')) {
                // Apply filter group and close dropdown
                const button = e.target.matches('[data-apply-filter-group]') ? e.target : e.target.closest('[data-apply-filter-group]');
                this.handleApplyFilterGroup(button);
            } else if (e.target.matches('[data-clear-filter-url]') || e.target.closest('[data-clear-filter-url]')) {
                // Clear filter using Shopify's url_to_remove
                e.preventDefault();
                const link = e.target.matches('[data-clear-filter-url]') ? e.target : e.target.closest('[data-clear-filter-url]');
                this.handleClearFilterURL(link);
            }
        });

        // Initialize mobile modal state
        this.initializeMobileModal();
    }

    /**
     * Handle individual filter changes (checkboxes AND range inputs)
     * This method precisely tracks which filter was changed to avoid race conditions
     */
    handleFilterChange(input) {
        // Check if this is a range input (price slider)
        if (input.type === 'range') {
            // For range inputs, synchronize with DOM and update immediately
            // The synchronizeWithDOM method reads current slider values
            this.synchronizeWithDOM();
            this.updateFilters();
        } else {
            // Handle as regular checkbox filter
            const filterParam = input.dataset.filterParam || input.name;
            const value = input.value;
            const isChecked = input.checked;

            // Queue the change to prevent race conditions
            this.queueFilterChange({
                type: 'filter',
                param: filterParam,
                value: value,
                checked: isChecked
            });
        }
    }

    /**
     * Handle price range changes
     */
    handlePriceChange(input) {
        const isPriceMin = input.hasAttribute('data-price-min');
        const isPriceMax = input.hasAttribute('data-price-max');

        if (!isPriceMin && !isPriceMax) return;

        // Convert display currency to cents
        const toCents = (val) => {
            if (val === undefined || val === null || val === '') return '';
            const n = Number(val);
            if (Number.isNaN(n)) return '';
            return Math.round(n * 100);
        };

        const value = toCents(input.value);
        const param = isPriceMin ? 'filter.v.price.gte' : 'filter.v.price.lte';

        // Queue the price change
        this.queueFilterChange({
            type: 'price',
            param: param,
            value: value !== '' ? String(value) : null
        });
    }

    /**
     * Queue filter changes and process them
     * This prevents race conditions when multiple changes happen quickly
     */
    queueFilterChange(change) {
        this.pendingChanges.push(change);

        // Process the queued changes
        this.processQueuedChanges();
    }

    /**
     * Process all queued filter changes
     */
    processQueuedChanges() {
        if (this.pendingChanges.length === 0) return;

        // Track if any change requires debouncing
        let requiresDebounce = false;

        // Process all pending changes
        while (this.pendingChanges.length > 0) {
            const change = this.pendingChanges.shift();
            this.applyFilterChange(change);

            // Check if this change type requires debouncing
            if (change.type === 'price') {
                requiresDebounce = true;
            }
        }

        // Update the filters after processing all changes
        if (requiresDebounce) {
            // Debounce price changes
            this.debouncedUpdate();
        } else {
            // Update immediately for other filter types
            this.updateFilters();
        }
    }

    /**
     * Apply a single filter change to the current parameters
     */
    applyFilterChange(change) {
        if (change.type === 'filter') {
            // Handle regular filter checkbox
            const existingValues = this.currentParams.getAll(change.param);

            if (change.checked) {
                // Add the value if not already present
                if (!existingValues.includes(change.value)) {
                    this.currentParams.append(change.param, change.value);
                }
            } else {
                // Remove only this specific value
                this.removeSpecificFilterValue(change.param, change.value);
            }
        } else if (change.type === 'price') {
            // Handle price range
            if (change.value !== null) {
                this.currentParams.set(change.param, change.value);
            } else {
                this.currentParams.delete(change.param);
            }
        } else if (change.type === 'sort') {
            // Handle sort
            this.currentParams.set('sort_by', change.value);
        }

        // Always remove page parameter when filters change
        this.currentParams.delete('page');
    }

    /**
     * Remove a specific value from a filter parameter
     * Handles multiple values correctly
     */
    removeSpecificFilterValue(param, valueToRemove) {
        const existingValues = this.currentParams.getAll(param);

        // Remove all instances of this parameter
        this.currentParams.delete(param);

        // Re-add all values except the one to remove
        existingValues.forEach(value => {
            if (value !== valueToRemove) {
                this.currentParams.append(param, value);
            }
        });
    }

    /**
     * Synchronize current parameters with DOM state
     * Used after section replacement to ensure consistency
     * All selectors are scoped to this component
     */
    synchronizeWithDOM() {
        // Build parameters from current DOM state
        const syncedParams = new URLSearchParams();

        // Copy non-filter parameters from current params
        for (const [key, value] of this.currentParams.entries()) {
            if (!key.startsWith('filter.') && key !== 'page' && key !== 'sections') {
                syncedParams.set(key, value);
            }
        }

        // Collect all checked filters from DOM (scoped to this component)
        const checkedInputs = Array.from(this.querySelectorAll('[data-filter-input]:checked'))
            .filter(el => !el.closest('.hidden, [hidden], [aria-hidden="true"]'));

        const filterGroups = new Map();
        checkedInputs.forEach(input => {
            const param = input.dataset.filterParam || input.name;
            const value = input.value;

            if (!filterGroups.has(param)) {
                filterGroups.set(param, []);
            }
            filterGroups.get(param).push(value);
        });

        // Add all filter values
        filterGroups.forEach((values, param) => {
            values.forEach(value => {
                syncedParams.append(param, value);
            });
        });

        // Handle price range (scoped to this component)
        // Values are already in the correct unit (pounds) - no conversion needed
        const minPrice = this.querySelector('[data-range-min]')?.value;
        const maxPrice = this.querySelector('[data-range-max]')?.value;

        if (minPrice !== undefined && minPrice !== null && minPrice !== '') {
            syncedParams.set('filter.v.price.gte', String(minPrice));
        }
        if (maxPrice !== undefined && maxPrice !== null && maxPrice !== '') {
            syncedParams.set('filter.v.price.lte', String(maxPrice));
        }

        // Handle sort (scoped to this component)
        const sortSelect = this.querySelector('[data-sort-select]');
        if (sortSelect?.value) {
            syncedParams.set('sort_by', sortSelect.value);
        }

        // Update current params to match synced state
        this.currentParams = syncedParams;
    }

    /**
     * Handle sort changes
     */
    handleSortChange(e) {
        const select = e.target;
        const selectedOption = select.options[select.selectedIndex];

        // Update display text immediately for instant feedback
        const displayValue = this.querySelector('[data-sort-display]');
        if (displayValue && selectedOption) {
            displayValue.textContent = selectedOption.text;
        }

        this.queueFilterChange({
            type: 'sort',
            value: select.value
        });
    }

    /**
     * Handle removing specific filter
     */
    handleRemoveFilter({ target }) {
        const param = target.dataset.removeFilter;
        const value = target.dataset.removeValue;

        this.removeSpecificFilterValue(param, value);
        this.updateFilters();
    }

    /**
     * Handle removing price filter
     */
    handleRemovePriceFilter(e) {
        this.currentParams.delete('filter.v.price.gte');
        this.currentParams.delete('filter.v.price.lte');
        this.updateFilters();
    }

    /**
     * Handle clearing all filters
     */
    handleClearAllFilters(e) {
        const keysToRemove = [];
        for (const key of this.currentParams.keys()) {
            if (key.startsWith('filter.') || key === 'page') {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(key => this.currentParams.delete(key));

        this.updateFilters();
    }

    /**
     * Update filters by fetching new content
     */
    async updateFilters(updateURL = true) {
        if (this.isLoading) {
            return;
        }

        // Clear any remaining pending changes since we're updating now
        this.pendingChanges = [];

        try {
            this.isLoading = true;

            // Start timing for filter operation
            console.time('filter');
            console.log('[CollectionFilter] 🔄 Starting filter update');

            this.showLoading();

            // Start loading state for product grid
            this.startGridLoading();

            // Build URL for section rendering API
            const url = this.buildSectionURL();

            console.log('[CollectionFilter] 📡 Fetching section');

            // Fetch updated section
            const htmlContent = await this.fetchSection(url);

            console.log('[CollectionFilter] ✅ Section fetched, preparing DOM swap');

            // Store current params before replacement
            const paramsToPreserve = new URLSearchParams(this.currentParams);

            // Store the current filter UI state
            const filterUIState = this.captureFilterUIState();

            console.log('[CollectionFilter] 🔄 Swapping HTML');

            // Replace this component with new version
            // The new component will automatically initialize via connectedCallback()!
            this.replaceComponent(htmlContent, paramsToPreserve, filterUIState);

            console.log('[CollectionFilter] ✅ HTML swapped, items will initialize');

            // Update browser URL
            if (updateURL) {
                this.updateBrowserURL();
            }

            console.timeEnd('filter');

        } catch (error) {
            console.error('Error updating filters:', error);
            this.showError('Failed to update filters. Please try again.');

            // On error, synchronize with current DOM state
            this.synchronizeWithDOM();

            // Ensure loading state is cleared on error
            this.finishGridLoading();
        } finally {
            this.isLoading = false;
            this.hideLoading();
        }
    }

    /**
     * Build URL for section rendering API
     */
    buildSectionURL() {
        const base = window.location.pathname;
        const params = new URLSearchParams(this.currentParams);
        params.delete('page');
        params.set('sections', this.sectionId);
        return `${base}?${params.toString()}`;
    }

    /**
     * Fetch section content
     */
    async fetchSection(url) {
        const response = await fetch(url, {
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
                // NO 'Accept: application/json' - this was the original bug!
            }
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        const sectionHtml = data[this.sectionId] || data['items'];

        if (!sectionHtml) {
            throw new Error(`Section "${this.sectionId}" not found in response`);
        }

        return sectionHtml;
    }

    /**
     * Capture current filter UI state (search terms, expanded states)
     */
    captureFilterUIState() {
        const state = {};

        // Capture search input values
        const searchInputs = this.querySelectorAll('[data-filter-search]');
        searchInputs.forEach(input => {
            const filterGroup = input.dataset.filterGroup;
            if (filterGroup && input.value) {
                if (!state.searches) state.searches = {};
                state.searches[filterGroup] = input.value;
            }
        });

        // Capture show more expanded states
        const showMoreButtons = this.querySelectorAll('[data-filter-more-items]');
        showMoreButtons.forEach(button => {
            if (button.dataset.expanded === 'true') {
                const filterGroup = button.closest('[data-filter-group]');
                if (filterGroup) {
                    const groupName = filterGroup.dataset.filterGroup;
                    if (!state.expanded) state.expanded = {};
                    state.expanded[groupName] = true;
                }
            }
        });

        return state;
    }

    /**
     * Restore filter UI state after replacement
     */
    restoreFilterUIState(state) {
        if (!state) return;

        // Restore search input values
        if (state.searches) {
            Object.entries(state.searches).forEach(([filterGroup, value]) => {
                const input = this.querySelector(`[data-filter-search][data-filter-group="${filterGroup}"]`);
                if (input) {
                    input.value = value;
                    // Trigger search to apply filtering
                    this.handleFilterSearch(input);
                }
            });
        }

        // Restore show more expanded states
        if (state.expanded) {
            Object.entries(state.expanded).forEach(([groupName, isExpanded]) => {
                if (isExpanded) {
                    const button = this.querySelector(`[data-filter-group="${groupName}"] [data-filter-more-items]`);
                    if (button && button.dataset.expanded !== 'true') {
                        this.handleShowMoreToggle(button);
                    }
                }
            });
        }
    }

    /**
     * Replace this component with new version
     * The new component will automatically initialize!
     */
    replaceComponent(htmlContent, paramsToPreserve = null, filterUIState = null) {
        // Create temporary container
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent;

        // Find new component in response
        const newComponent = tempDiv.querySelector('collection-filter');

        if (!newComponent) {
            throw new Error('New collection-filter component not found in response');
        }

        // If we have params to preserve, set them on the new component
        if (paramsToPreserve) {
            // Store params in a data attribute for the new component to pick up
            newComponent.dataset.preservedParams = paramsToPreserve.toString();
        }

        // Store UI state for restoration
        if (filterUIState) {
            newComponent.dataset.filterUIState = JSON.stringify(filterUIState);
        }

        // Replace this component (outerHTML is unreliable for custom elements)
        if (this.parentNode) {
            this.parentNode.replaceChild(newComponent, this);

            // After replacement, ensure the new component has the correct params
            if (paramsToPreserve) {
                newComponent.currentParams = paramsToPreserve;
                delete newComponent.dataset.preservedParams;
            }

            // Restore UI state
            if (filterUIState) {
                setTimeout(() => {
                    newComponent.restoreFilterUIState(filterUIState);
                    delete newComponent.dataset.filterUIState;
                }, 0);
            }
        } else {
            throw new Error('Cannot replace component - no parent node');
        }

        // Defer product item refresh to allow DOM to settle AND skeleton to paint
        // Use requestAnimationFrame to ensure at least one paint cycle
        requestAnimationFrame(() => {
            // Optional: add extra delay to make skeletons more visible (200-300ms recommended)
            setTimeout(() => {
                if (window.productItemRenderer && typeof window.productItemRenderer.refresh === 'function') {
                    console.log('[CollectionFilter] 🎯 Calling productItemRenderer.refresh()');
                    window.productItemRenderer.refresh();
                }
            }, 200); // 200ms delay allows skeletons to be visible during hydration
        });
    }

    /**
     * Update browser URL
     */
    updateBrowserURL() {
        const url = new URL(window.location);

        // Clear existing filter parameters
        const keysToRemove = [];
        for (const key of url.searchParams.keys()) {
            if (key.startsWith('filter.') || key === 'page') {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(key => url.searchParams.delete(key));

        // Add current parameters (preserve repeated filter keys)
        const keys = Array.from(new Set([...this.currentParams.keys()]));
        keys.forEach((key) => {
            if (key === 'sections') return;
            const values = this.currentParams.getAll(key);
            if (key.startsWith('filter.')) {
                values.forEach((v) => url.searchParams.append(key, v));
            } else {
                url.searchParams.set(key, values[values.length - 1]);
            }
        });

        history.pushState({}, '', url);
    }

    /**
     * Show loading state
     */
    showLoading() {
        this.classList.add('is--loading');
    }

    /**
     * Hide loading state
     */
    hideLoading() {
        this.classList.remove('is--loading');
    }

    /**
     * Start loading state for product grid
     */
    startGridLoading() {
        if (typeof window.ProductGridLoading === 'undefined') {
            console.warn('[CollectionFilter] ProductGridLoading not available');
            return;
        }

        // Target grid by data attribute (primary) or class (fallback)
        const grid = this.querySelector('[data-collection-product-grid], .product-grid');
        if (grid) {
            window.ProductGridLoading.startLoading(grid, 'filter');
        }
    }

    /**
     * Finish loading state for product grid
     */
    finishGridLoading() {
        if (typeof window.ProductGridLoading === 'undefined') {
            return;
        }

        // Target grid by data attribute (primary) or class (fallback)
        const grid = this.querySelector('[data-collection-product-grid], .product-grid');
        if (grid) {
            window.ProductGridLoading.finishLoading(grid);
        }
    }

    /**
     * Show error message
     */
    showError(message) {
        console.error(message);
    }

    /**
     * Initialize price range slider visual updates and preset buttons
     * Based on reference implementation pattern
     */
    initPriceRangeSlider() {
        const container = this.querySelector('[data-price-range-filter]');
        if (!container) return;

        // Prevent double initialization
        if (container.dataset.initialized === 'true') return;
        container.dataset.initialized = 'true';

        const minRange = container.querySelector('[data-range-min]');
        const maxRange = container.querySelector('[data-range-max]');
        const minDisplay = container.querySelector('[data-min-display]');
        const maxDisplay = container.querySelector('[data-max-display]');
        const sliderRange = container.querySelector('[data-slider-range]');
        const presetButtons = container.querySelectorAll('[data-price-preset]');

        if (!minRange || !maxRange) return;

        const rangeMaxValue = parseInt(maxRange.max);

        // Format price for display
        const formatPrice = (value) => {
            const pounds = parseFloat(value);
            if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
                return Shopify.formatMoney(pounds * 100, window.theme?.moneyFormat || '£{{amount}}');
            }
            return `£${pounds.toFixed(2)}`;
        };

        // Update the visual slider range bar position
        const updateSliderRange = () => {
            const min = parseInt(minRange.value);
            const max = parseInt(maxRange.value);
            const percent1 = (min / rangeMaxValue) * 100;
            const percent2 = (max / rangeMaxValue) * 100;

            // Position bar between the two thumb centres
            sliderRange.style.left = percent1 + '%';
            sliderRange.style.right = (100 - percent2) + '%';
        };

        // Update price displays
        const updatePriceDisplays = () => {
            minDisplay.textContent = formatPrice(minRange.value);
            maxDisplay.textContent = formatPrice(maxRange.value);
            updateSliderRange();
        };

        // Clear active preset button
        const clearActivePreset = () => {
            presetButtons.forEach(btn => btn.classList.remove('active'));
        };

        // Check if current values match any preset
        const updateActivePreset = () => {
            const currentMin = parseInt(minRange.value);
            const currentMax = parseInt(maxRange.value);

            clearActivePreset();

            presetButtons.forEach(button => {
                const [presetMin, presetMax] = button.getAttribute('data-price-preset')
                    .split('-')
                    .map(v => parseInt(v));

                // Allow ±1 tolerance for rounding
                if (Math.abs(currentMin - presetMin) <= 1 &&
                    Math.abs(currentMax - presetMax) <= 1) {
                    button.classList.add('active');
                }
            });
        };

        // Min range input handler
        minRange.addEventListener('input', () => {
            if (parseInt(minRange.value) >= parseInt(maxRange.value)) {
                minRange.value = parseInt(maxRange.value) - 1;
            }
            updatePriceDisplays();
            clearActivePreset();
        });

        // Max range input handler
        maxRange.addEventListener('input', () => {
            if (parseInt(maxRange.value) <= parseInt(minRange.value)) {
                maxRange.value = parseInt(minRange.value) + 1;
            }
            updatePriceDisplays();
            clearActivePreset();
        });

        // Preset button handlers
        presetButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();

                const [min, max] = button.getAttribute('data-price-preset').split('-');

                // Set slider values
                minRange.value = min;
                maxRange.value = parseInt(max) > rangeMaxValue ? rangeMaxValue : max;

                // Update displays
                updatePriceDisplays();

                // Toggle active state
                clearActivePreset();
                button.classList.add('active');

                // Trigger change events for BOTH sliders to update filters
                minRange.dispatchEvent(new Event('change', { bubbles: true }));
                maxRange.dispatchEvent(new Event('change', { bubbles: true }));

                // Close dropdown if button has data-close-on-click
                if (button.hasAttribute('data-close-on-click')) {
                    const filterItem = button.closest('[data-filter-item]');
                    if (filterItem) {
                        const titleButton = filterItem.querySelector('[data-filter-item-title]');
                        if (titleButton && titleButton.classList.contains('is-active')) {
                            titleButton.click();
                        }
                    }
                }
            });
        });

        // Initialize displays and active states
        updatePriceDisplays();
        updateActivePreset();
    }

    /**
     * Initialize filter UI enhancements
     * Works with Liquid-rendered HTML that already has items hidden
     */
    initializeFilterUI() {
        // Process each filter group within this component (using data attribute)
        const filterGroups = this.querySelectorAll('[data-filter-group]');

        filterGroups.forEach((group, index) => {

            // CRITICAL: Mark items that are originally hidden by Liquid
            // This must happen ONCE at initialization, not during search
            const allFilterItems = group.querySelectorAll('label[data-filter-item]');
            let hiddenByLiquidCount = 0;

            allFilterItems.forEach(item => {
                if (item.classList.contains('is--hidden')) {
                    // This item was hidden by Liquid's filter_values_to_show
                    item.dataset.originallyHidden = 'true';
                    hiddenByLiquidCount++;
                }
            });

                        // Initialize show more button state
            const showMoreButton = group.querySelector('[data-filter-more-items]');
            if (showMoreButton) {
                if (hiddenByLiquidCount === 0) {
                    // No items are hidden, hide the button
                    showMoreButton.classList.add('is--hidden');
                } else {
                    // There are hidden items, show the button
                    showMoreButton.classList.remove('is--hidden');
                }

                // Set initial state to collapsed
                showMoreButton.dataset.expanded = 'false';
            }

            // Clear search inputs on init
            const searchInput = group.querySelector('[data-filter-search]');
            if (searchInput) {
                searchInput.value = '';
                // Ensure clear button is hidden initially
                this.updateSearchClearButton(searchInput, false);
                            }

            // Ensure hidden items are properly hidden initially
            const hiddenItems = group.querySelectorAll('label[data-filter-item].is--hidden');
            hiddenItems.forEach(item => {
                item.classList.add('is--hidden');
            });
        });
    }

    /**
     * Handle show more/less toggle
     * Simple toggle that shows/hides items marked as originally hidden
     */
    handleShowMoreToggle(button) {
        const filterGroup = button.closest('[data-filter-group]');
        if (!filterGroup) {
            console.warn('Show more button has no filter group parent');
            return;
        }

        const isExpanded = button.dataset.expanded === 'true';
        const showText = button.querySelector('[data-show-text]');
        const hideText = button.querySelector('[data-hide-text]');

        // Find items that were originally hidden by Liquid
        const originallyHiddenItems = filterGroup.querySelectorAll('label[data-filter-item][data-originally-hidden="true"]');

        if (isExpanded) {
            // COLLAPSE: Hide the extra items again
            button.dataset.expanded = 'false';

            // Hide the items that were originally hidden
            originallyHiddenItems.forEach(item => {
                item.classList.add('is--hidden');
            });

            // Update button text - show "Show more", hide "Show less"
            if (showText) {
                showText.classList.remove('filter-ui-hidden');
            }
            if (hideText) {
                hideText.classList.add('filter-ui-hidden');
            }
        } else {
            // EXPAND: Show all items
            button.dataset.expanded = 'true';

            // Show the originally hidden items (unless hidden by search)
            originallyHiddenItems.forEach(item => {
                if (!item.classList.contains('is--hidden-by-search')) {
                    item.classList.remove('is--hidden');
                }
            });

            // Update button text - hide "Show more", show "Show less"
            if (showText) {
                showText.classList.add('filter-ui-hidden');
            }
            if (hideText) {
                hideText.classList.remove('filter-ui-hidden');
            }
        }

        // If collapsing, restore hidden state
        if (isExpanded) {
            const expandedItems = filterGroup.querySelectorAll('label[data-filter-item][data-expanded-by-show-more="true"]');
            expandedItems.forEach(item => {
                if (!item.classList.contains('is--hidden-by-search')) {
                    item.classList.add('is--hidden');
                }
                delete item.dataset.expandedByShowMore;
            });
        }
    }

    /**
     * Handle filter search
     * Searches through all filter options and shows/hides them using CSS classes
     * When searching: show ALL matches, hide "Show more"
     * When cleared: restore original N-item limit, show "Show more" if needed
     */
    handleFilterSearch(input) {
        const searchTerm = input.value.toLowerCase().trim();

        // Update clear button visibility
        this.updateSearchClearButton(input, searchTerm.length > 0);

        // Find the filter group
        let filterGroup = input.closest('[data-filter-group]');
        if (!filterGroup && input.dataset.filterGroup) {
            filterGroup = input.closest('.filter-options, .filter-group');
        }

        if (!filterGroup) {
            return;
        }

        const groupIdentifier = input.dataset.filterGroup || filterGroup.dataset.filterGroup;

        // Find ALL filter items in this group
        let filterOptions = filterGroup.querySelectorAll('label[data-filter-item]');

        // If not found, look in parent container
        if (filterOptions.length === 0) {
            const parentContainer = filterGroup.parentElement;
            if (parentContainer) {
                filterOptions = parentContainer.querySelectorAll('label[data-filter-item]');
            }
        }

        // If still not found, look by input name
        if (filterOptions.length === 0 && groupIdentifier) {
            const matchingInputs = this.querySelectorAll(`input[name="${groupIdentifier}"]`);
            const labels = [];
            matchingInputs.forEach(input => {
                const label = input.closest('label[data-filter-item]');
                if (label) labels.push(label);
            });
            filterOptions = labels;
        }

        // Find the show more button - try multiple strategies
        let showMoreButton = filterGroup.querySelector('[data-filter-more-items]');
        if (!showMoreButton && filterGroup.parentElement) {
            showMoreButton = filterGroup.parentElement.querySelector('[data-filter-more-items]');
        }
        // Try looking in the whole component if still not found
        if (!showMoreButton) {
            showMoreButton = this.querySelector(`[data-filter-group="${groupIdentifier}"] [data-filter-more-items]`);
        }

        let visibleMatchCount = 0;
        let totalItems = filterOptions.length;

        // Process each filter option
        filterOptions.forEach((option, index) => {
            const labelSpan = option.querySelector('.filter-option__label');
            const filterValue = option.dataset.filterValue || '';
            const labelText = labelSpan ? labelSpan.textContent.toLowerCase().trim() : '';
            const valueText = filterValue.toLowerCase();

            // Check if matches search
            const matchesSearch = searchTerm === '' ||
                          labelText.includes(searchTerm) ||
                          valueText.includes(searchTerm);

            // Check if originally hidden by Liquid (this was set during initialization)
            const wasOriginallyHidden = option.dataset.originallyHidden === 'true';

            if (searchTerm !== '') {
                // ACTIVE SEARCH MODE
                if (matchesSearch) {
                    // Show ALL matches regardless of original state
                    option.classList.remove('is--hidden', 'is--hidden-by-search');
                    visibleMatchCount++;
                } else {
                    // Hide non-matches
                    option.classList.add('is--hidden-by-search');
                }
            } else {
                // NO SEARCH - Restore original state
                option.classList.remove('is--hidden-by-search');

                if (wasOriginallyHidden) {
                    // This item was originally hidden by Liquid
                    // Check if "Show more" is expanded
                    if (showMoreButton && showMoreButton.dataset.expanded === 'true') {
                        // Show more is expanded, show the item
                        option.classList.remove('is--hidden');
                    } else {
                        // Show more is collapsed, hide the item
                        option.classList.add('is--hidden');
                    }
                } else {
                    // Item was not originally hidden, always show it
                    option.classList.remove('is--hidden');
                    visibleMatchCount++;
                }
            }
        });

        // Update show more button visibility and state
        if (showMoreButton) {
            if (searchTerm !== '') {
                // ACTIVE SEARCH: Always hide the button and disable it
                showMoreButton.classList.add('is--hidden');
                showMoreButton.setAttribute('disabled', 'true');
                showMoreButton.style.pointerEvents = 'none';
            } else {
                // NO SEARCH: Check if we need the button

                // Re-enable button
                showMoreButton.removeAttribute('disabled');
                showMoreButton.style.pointerEvents = '';

                // Count items that were originally hidden by Liquid
                const itemsOriginallyHidden = Array.from(filterOptions).filter(
                    opt => opt.dataset.originallyHidden === 'true'
                );

                if (itemsOriginallyHidden.length > 0) {
                    // There are items that can be shown/hidden
                    showMoreButton.classList.remove('is--hidden');

                    // Ensure proper text is shown based on current state
                    const showText = showMoreButton.querySelector('[data-show-text]');
                    const hideText = showMoreButton.querySelector('[data-hide-text]');
                    const isExpanded = showMoreButton.dataset.expanded === 'true';

                    if (isExpanded) {
                        // Button is expanded - show "Show less"
                        if (showText) showText.classList.add('filter-ui-hidden');
                        if (hideText) hideText.classList.remove('filter-ui-hidden');
                    } else {
                        // Button is collapsed - show "Show more"
                        if (showText) showText.classList.remove('filter-ui-hidden');
                        if (hideText) hideText.classList.add('filter-ui-hidden');
                    }
                } else {
                    // No items to show/hide
                    showMoreButton.classList.add('is--hidden');
                }
            }
        }
    }

    /**
     * Update or create the clear button for search input
     */
    updateSearchClearButton(input, show) {
        const wrapper = input.parentElement;
        let clearButton = wrapper.querySelector('[data-search-clear]');

        if (show && !clearButton) {
            // Create clear button if it doesn't exist
            clearButton = document.createElement('button');
            clearButton.type = 'button';
            clearButton.dataset.searchClear = '';
            clearButton.dataset.searchInput = input.dataset.filterGroup || '';
            clearButton.className = 'filter-search-clear';
            clearButton.innerHTML = `
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
            `;
            clearButton.setAttribute('aria-label', 'Clear search');

            // Add wrapper class if needed
            if (!wrapper.classList.contains('filter-search-wrapper')) {
                wrapper.classList.add('filter-search-wrapper');
            }

            wrapper.appendChild(clearButton);
        } else if (!show && clearButton) {
            // Hide clear button
            clearButton.classList.add('filter-ui-hidden');
        } else if (show && clearButton) {
            // Show clear button
            clearButton.classList.remove('filter-ui-hidden');
        }
    }

    /**
     * Handle clicking the search clear button
     */
    handleSearchClear(button) {
        const inputGroup = button.dataset.filterGroup;
        let searchInput;

        // Find the associated search input
        if (inputGroup) {
            searchInput = this.querySelector(`[data-filter-search][data-filter-group="${inputGroup}"]`);
        } else {
            // Fallback: find the closest search input
            searchInput = button.parentElement.querySelector('[data-filter-search]');
        }

        if (searchInput) {
            searchInput.value = '';

            // Reset "Show more" button to collapsed state
            const filterGroup = searchInput.closest('[data-filter-group]') || searchInput.closest('.filter-options, .filter-group');
            if (filterGroup) {
                const showMoreButton = filterGroup.querySelector('[data-filter-more-items]');
                if (showMoreButton && showMoreButton.dataset.expanded === 'true') {
                    showMoreButton.dataset.expanded = 'false';
                    const showText = showMoreButton.querySelector('[data-show-text]');
                    const hideText = showMoreButton.querySelector('[data-hide-text]');
                    if (showText) showText.classList.remove('filter-ui-hidden');
                    if (hideText) hideText.classList.add('filter-ui-hidden');
                }
            }

            // Hide the clear button
            this.updateSearchClearButton(searchInput, false);
            // Trigger search to reset filters
            this.handleFilterSearch(searchInput);
        }
    }

    /**
     * Capture filter UI state before section update
     * Uses component-scoped selectors
     */
    captureFilterUIState() {
        const state = {};

        // Capture search values from within this component
        const searchInputs = this.querySelectorAll('[data-filter-search]');
        searchInputs.forEach(input => {
            if (input.value) {
                const group = input.closest('[data-filter-group]');
                if (group) {
                    const groupName = group.dataset.filterGroup;
                    if (!state.searches) state.searches = {};
                    state.searches[groupName] = input.value;
                }
            }
        });

        // Capture expanded states from within this component
        const showMoreButtons = this.querySelectorAll('[data-filter-more-items]');
        showMoreButtons.forEach(button => {
            if (button.dataset.expanded === 'true') {
                const group = button.closest('[data-filter-group]');
                if (group) {
                    const groupName = group.dataset.filterGroup;
                    if (!state.expanded) state.expanded = {};
                    state.expanded[groupName] = true;
                }
            }
        });

        return state;
    }

    /**
     * Restore filter UI state after section update
     * Uses component-scoped selectors
     */
    restoreFilterUIState(state) {
        if (!state) return;

        // Wait for next tick to ensure DOM is ready
        setTimeout(() => {
            // Restore search values and apply filtering (scoped to this component)
            if (state.searches) {
                Object.entries(state.searches).forEach(([groupName, value]) => {
                    const input = this.querySelector(`[data-filter-group="${groupName}"] [data-filter-search]`);
                    if (input) {
                        input.value = value;
                        this.handleFilterSearch(input);
                    }
                });
            }

            // Restore expanded states (scoped to this component)
            if (state.expanded) {
                Object.entries(state.expanded).forEach(([groupName, isExpanded]) => {
                    if (isExpanded) {
                        const button = this.querySelector(`[data-filter-group="${groupName}"] [data-filter-more-items]`);
                        if (button && button.dataset.expanded !== 'true') {
                            this.handleShowMoreToggle(button);
                        }
                    }
                });
            }
        }, 0);
    }

    /**
     * Handle filter group header toggle (collapse/expand)
     */
    handleFilterGroupToggle(header) {
        const filterGroup = header.parentElement;
        if (!filterGroup || !filterGroup.classList.contains('filter-group')) {
            return;
        }

        const content = filterGroup.querySelector('.filter-group__content');
        if (!content) {
            return;
        }

        // Toggle collapsed state
        const isCollapsed = filterGroup.classList.contains('collapsed');

        if (isCollapsed) {
            // Expand
            filterGroup.classList.remove('collapsed');
            content.classList.remove('collapsed');

            // Update icon wrapper
            const iconWrapper = header.querySelector('.filter-group__icon-wrapper');
            if (iconWrapper) {
                iconWrapper.classList.remove('is-collapsed');
            }

            // Update aria-expanded
            header.setAttribute('aria-expanded', 'true');

            // Store expanded state
            this.storeFilterGroupState(filterGroup, false);
        } else {
            // Collapse
            filterGroup.classList.add('collapsed');
            content.classList.add('collapsed');

            // Update icon wrapper
            const iconWrapper = header.querySelector('.filter-group__icon-wrapper');
            if (iconWrapper) {
                iconWrapper.classList.add('is-collapsed');
            }

            // Update aria-expanded
            header.setAttribute('aria-expanded', 'false');

            // Store collapsed state
            this.storeFilterGroupState(filterGroup, true);
        }
    }

    /**
     * Store filter group collapse state
     */
    storeFilterGroupState(filterGroup, isCollapsed) {
        const filterName = filterGroup.dataset.filterGroup;
        if (!filterName) return;

        // Store in localStorage for persistence
        const storageKey = 'filter-collapse-state';
        let states = {};

        try {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                states = JSON.parse(stored);
            }
        } catch (e) {
            // Ignore localStorage errors
        }

        states[filterName] = isCollapsed;

        try {
            localStorage.setItem(storageKey, JSON.stringify(states));
        } catch (e) {
            // Ignore localStorage errors
        }
    }

    /**
     * Restore filter group collapse states
     */
    restoreFilterGroupStates() {
        const storageKey = 'filter-collapse-state';
        let states = {};

        try {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                states = JSON.parse(stored);
            }
        } catch (e) {
            // Ignore localStorage errors
            return;
        }

        // Apply stored states
        Object.keys(states).forEach(filterName => {
            const filterGroup = this.querySelector(`[data-filter-group="${filterName}"]`);
            if (filterGroup) {
                const content = filterGroup.querySelector('.filter-group__content');
                const header = filterGroup.querySelector('.filter-group__header');
                const iconWrapper = header?.querySelector('.filter-group__icon-wrapper');

                if (states[filterName]) {
                    // Apply collapsed state
                    filterGroup.classList.add('collapsed');
                    if (content) content.classList.add('collapsed');
                    if (iconWrapper) iconWrapper.classList.add('is-collapsed');
                    if (header) header.setAttribute('aria-expanded', 'false');
                } else {
                    // Apply expanded state
                    filterGroup.classList.remove('collapsed');
                    if (content) content.classList.remove('collapsed');
                    if (iconWrapper) iconWrapper.classList.remove('is-collapsed');
                    if (header) header.setAttribute('aria-expanded', 'true');
                }
            }
        });
    }

    /**
     * Initialize mobile modal functionality
     */
    initializeMobileModal() {
        // Find the filter modal
        this.filterModal = document.querySelector('#filter-modal');
        if (!this.filterModal) {
            return;
        }

        // Find modal content container
        this.modalContent = document.querySelector('#modal-content');

        // Set initial state
        this.currentFilterView = null;

        // Close modal when clicking overlay
        const overlay = document.getElementById('modal-overlay');
        if (overlay) {
            overlay.addEventListener('click', () => this.handleMobileModalClose());
        }
    }

    /**
     * Handle opening mobile filter modal
     */
    handleMobileFilterOpen(button) {
        if (!this.filterModal) return;

        const filterType = button.dataset.mobileFilter;
                // Open modal
        this.openModal();

        if (filterType && filterType !== 'all') {
            // Show specific filter content
            this.showMobileFilterDetail(filterType);
        } else {
            // Show main filter list
            this.showMobileFilterList();
        }
    }

    /**
     * Hide Shopify chat widget (injected by Shopify, not in theme code)
     */
    hideShopifyChat() {
        var chat = document.getElementById('shopify-chat') || document.querySelector('shopify-chat');
        if (chat) chat.style.display = 'none';
    }

    /**
     * Show Shopify chat widget
     */
    showShopifyChat() {
        var chat = document.getElementById('shopify-chat') || document.querySelector('shopify-chat');
        if (chat) chat.style.display = '';
    }

    /**
     * Open the modal with CSS state classes
     */
    openModal() {
        if (!this.filterModal) return;

        const overlay = document.getElementById('modal-overlay');

        // Show modal using CSS state class
        this.filterModal.classList.add('is-open');

        // Show overlay using CSS state class
        if (overlay) {
            overlay.classList.add('is-active');
        }

        // Prevent body scroll and hide chat widget
        document.body.classList.add('overflow-hidden', 'modal--open');
        this.hideShopifyChat();
    }

    /**
     * Handle closing mobile modal
     */
    handleMobileModalClose() {
        if (!this.filterModal) return;

        const overlay = document.getElementById('modal-overlay');

        // Hide modal using CSS state class
        this.filterModal.classList.remove('is-open');

        // Hide overlay using CSS state class
        if (overlay) {
            overlay.classList.remove('is-active');
        }

        // After transition, reset content
        setTimeout(() => {
            // Reset all content states
            this.resetModalContent();
        }, 300); // Match CSS transition duration

        // Re-enable body scroll and show chat widget
        document.body.classList.remove('overflow-hidden', 'modal--open');
        this.showShopifyChat();
    }

    /**
     * Reset all modal content to initial state
     */
    resetModalContent() {
        // Show filter list view
        this.showMobileFilterList();
    }

    /**
     * Handle showing specific filter
     */
    handleShowFilter(button) {
        const filterType = button.dataset.showFilter;
        if (!filterType) return;

                this.showMobileFilterDetail(filterType);
    }

    /**
     * Show mobile filter list (main view)
     */
    showMobileFilterList() {
        // Hide all filter details using CSS state class
        const filterDetails = document.querySelectorAll('[data-filter-detail]');
        filterDetails.forEach(detail => {
            detail.classList.remove('is-active');
        });

        // Show the main filter list using CSS state class
        const filterList = document.getElementById('filter-list-view');
        if (filterList) {
            filterList.classList.add('is-active');
        }

        // Remove detail view state from modal
        if (this.filterModal) {
            this.filterModal.classList.remove('is-detail-view');
        }

        // Hide back button and update title using CSS state class
        const backButton = document.getElementById('back-btn');
        const modalTitle = document.getElementById('modal-title');

        if (backButton) {
            backButton.classList.remove('is-visible');
        }

        if (modalTitle) {
            modalTitle.textContent = 'Filter by';
        }

        // Reset current view
        this.currentFilterView = null;

            }

    /**
     * Show specific mobile filter detail
     */
    showMobileFilterDetail(filterType) {
                // Hide main list using CSS state class
        const filterList = document.getElementById('filter-list-view');
        if (filterList) {
            filterList.classList.remove('is-active');
        }

        // Hide all filter details first using CSS state class
        const allDetails = document.querySelectorAll('[data-filter-detail]');
        allDetails.forEach(detail => {
            detail.classList.remove('is-active');
        });

        // Show the specific filter detail using CSS state class
        const filterDetail = document.querySelector(`[data-filter-detail="${filterType}"]`);
        if (filterDetail) {
            filterDetail.classList.add('is-active');
            this.currentFilterView = filterType;
                    } else {
                    }

        // Add detail view state to modal (for centering title)
        if (this.filterModal) {
            this.filterModal.classList.add('is-detail-view');
        }

        // Show back button and update title using CSS state class
        const backButton = document.getElementById('back-btn');
        const modalTitle = document.getElementById('modal-title');

        if (backButton) {
            backButton.classList.add('is-visible');
        }

        if (modalTitle) {
            // Get filter label from the button
            const filterButton = document.querySelector(`[data-show-filter="${filterType}"]`);
            if (filterButton) {
                const filterLabel = filterButton.querySelector('span')?.textContent;
                modalTitle.textContent = filterLabel || 'Filter';
            }
        }
    }

    /**
     * Handle mobile back button
     */
    handleMobileBack() {
        this.showMobileFilterList();
    }

    /**
     * Handle apply filters button in mobile modal
     */
    handleApplyMobileFilters() {
        // Close the modal - filters are already applied in real-time
        this.handleMobileModalClose();
    }

    /**
     * Handle clear current filter in mobile modal
     */
    handleClearCurrentFilter() {
                if (!this.currentFilterView) {
            // If on main list, clear all filters
            this.handleClearAllFilters();
            return;
        }

        // Handle sort differently - reset to default
        if (this.currentFilterView === 'sort_by') {
            this.currentParams.delete('sort_by');
            this.updateFilters();
            return;
        }

        // Clear only the current filter being viewed
        const filterDetail = document.querySelector(`[data-filter-detail="${this.currentFilterView}"]`);
        if (filterDetail) {
            // Find all checkboxes in this filter and uncheck them
            const checkboxes = filterDetail.querySelectorAll('input[type="checkbox"]:checked');
            checkboxes.forEach(checkbox => {
                checkbox.checked = false;
                // Trigger change event to update filters
                checkbox.dispatchEvent(new Event('change', { bubbles: true }));
            });

            // Handle price range if it's a price filter
            const priceInputs = filterDetail.querySelectorAll('[data-price-min], [data-price-max]');
            if (priceInputs.length > 0) {
                priceInputs.forEach(input => {
                    const isMin = input.hasAttribute('data-price-min');
                    if (isMin) {
                        input.value = input.min;
                    } else {
                        input.value = input.max;
                    }
                    input.dispatchEvent(new Event('input', { bubbles: true }));
                });
            }
        }
    }

    /**
     * Handle drawer toggle
     */
    handleDrawerToggle() {
        const drawer = document.querySelector('[data-filter-drawer]');
        const overlay = document.querySelector('[data-filter-overlay]');

        if (!drawer) return;

        const isOpen = drawer.classList.contains('is-open');

        if (isOpen) {
            this.handleDrawerClose();
        } else {
            drawer.classList.add('is-open');
            if (overlay) overlay.classList.add('is-active');
            document.body.classList.add('overflow-hidden');
            this.hideShopifyChat();
        }
    }

    /**
     * Handle drawer close
     */
    handleDrawerClose() {
        const drawer = document.querySelector('[data-filter-drawer]');
        const overlay = document.querySelector('[data-filter-overlay]');

        if (!drawer) return;

        drawer.classList.remove('is-open');
        if (overlay) overlay.classList.remove('is-active');
        document.body.classList.remove('overflow-hidden');
        this.showShopifyChat();
    }

    /**
     * Handle dropdown toggle
     */
    handleDropdownToggle(toggleButton) {
        const dropdownId = toggleButton.dataset.filterDropdownToggle;
        const dropdown = document.getElementById(dropdownId);

        if (!dropdown) return;

        const isOpen = dropdown.classList.contains('is-open');

        // Close all other dropdowns
        document.querySelectorAll('.filter-dropdown.is-open').forEach(d => {
            if (d !== dropdown) {
                d.classList.remove('is-open');
                d.previousElementSibling?.setAttribute('aria-expanded', 'false');
            }
        });

        // Toggle current dropdown
        if (isOpen) {
            dropdown.classList.remove('is-open');
            toggleButton.setAttribute('aria-expanded', 'false');
        } else {
            dropdown.classList.add('is-open');
            toggleButton.setAttribute('aria-expanded', 'true');
        }
    }

    /**
     * Handle closing a specific dropdown via close button
     */
    handleDropdownClose(closeButton) {
        const dropdown = closeButton.closest('.filter-dropdown');
        if (dropdown) {
            dropdown.classList.remove('is-open');
            const toggle = document.querySelector(`[data-filter-dropdown-toggle="${dropdown.id}"]`);
            if (toggle) toggle.setAttribute('aria-expanded', 'false');
        }
    }

    /**
     * Handle apply filter group (close dropdown after applying)
     */
    handleApplyFilterGroup(button) {
        const dropdown = button.closest('.filter-dropdown');
        if (dropdown) {
            dropdown.classList.remove('is-open');
            const toggle = document.querySelector(`[data-filter-dropdown-toggle="${dropdown.id}"]`);
            if (toggle) toggle.setAttribute('aria-expanded', 'false');
        }
    }

    /**
     * Close all dropdowns when clicking outside
     */
    handleClickOutside(event) {
        // Don't close if clicking inside a dropdown or on a toggle button
        if (event.target.closest('.filter-dropdown') ||
            event.target.closest('[data-filter-dropdown-toggle]')) {
            return;
        }

        // Don't interfere with mobile menu drilldown navigation
        if (event.target.closest('[data-mobile-menu-drilldown]') ||
            event.target.closest('[data-mobile-menu-trigger]')) {
            return;
        }

        // Close all open dropdowns
        document.querySelectorAll('.filter-dropdown.is-open').forEach(dropdown => {
            dropdown.classList.remove('is-open');
            const toggle = document.querySelector(`[data-filter-dropdown-toggle="${dropdown.id}"]`);
            if (toggle) toggle.setAttribute('aria-expanded', 'false');
        });
    }

    /**
     * Handle clear filter group
     */
    /**
     * Handle clearing filter using Shopify's url_to_remove
     * This is the proper way - let Shopify manage the URL construction
     */
    handleClearFilterURL(link) {
        const url = new URL(link.href, window.location.origin);

        // Update our current params to match the cleared state
        this.currentParams = new URLSearchParams(url.search);

        // Trigger section re-render with the new URL
        this.updateFilters();
    }

    /**
     * Initialize Price Range Slider
     * Handles visual updates and preset buttons
     * Price conversion to pence is handled by handlePriceChange()
     */
    initPriceRangeSlider() {
        const container = this.querySelector('[data-price-range-filter]');
        if (!container) return;

        // Prevent double initialization
        if (container.dataset.initialized === 'true') return;
        container.dataset.initialized = 'true';

        const minRange = container.querySelector('[data-range-min]');
        const maxRange = container.querySelector('[data-range-max]');
        const minDisplay = container.querySelector('[data-min-display]');
        const maxDisplay = container.querySelector('[data-max-display]');
        const sliderRange = container.querySelector('[data-slider-range]');
        const presetButtons = container.querySelectorAll('[data-price-preset]');

        if (!minRange || !maxRange) return;

        const rangeMaxValue = parseInt(maxRange.max);

        // Format price for display
        const formatPrice = (value) => {
            return `£${parseFloat(value).toFixed(2)}`;
        };

        // Update the visual slider range bar
        const updateSliderRange = () => {
            const min = parseInt(minRange.value);
            const max = parseInt(maxRange.value);
            const percent1 = (min / rangeMaxValue) * 100;
            const percent2 = (max / rangeMaxValue) * 100;

            sliderRange.style.left = percent1 + '%';
            sliderRange.style.right = (100 - percent2) + '%';
        };

        // Update price displays
        const updatePriceDisplays = () => {
            minDisplay.textContent = formatPrice(minRange.value);
            maxDisplay.textContent = formatPrice(maxRange.value);
            updateSliderRange();
        };

        // Clear active preset button
        const clearActivePreset = () => {
            presetButtons.forEach(btn => btn.classList.remove('active'));
        };

        // Check if current range matches any preset and mark it active
        const updateActivePreset = () => {
            const currentMin = parseInt(minRange.value);
            const currentMax = parseInt(maxRange.value);

            clearActivePreset();

            presetButtons.forEach(button => {
                const [presetMin, presetMax] = button.getAttribute('data-price-preset')
                    .split('-')
                    .map(v => parseInt(v));

                // Check if current range matches this preset (within 1 unit tolerance)
                if (Math.abs(currentMin - presetMin) <= 1 && Math.abs(currentMax - presetMax) <= 1) {
                    button.classList.add('active');
                }
            });
        };

        // Min range input handler - prevent crossing
        minRange.addEventListener('input', () => {
            if (parseInt(minRange.value) >= parseInt(maxRange.value)) {
                minRange.value = parseInt(maxRange.value) - 1;
            }
            updatePriceDisplays();
            clearActivePreset();
        });

        // Max range input handler - prevent crossing
        maxRange.addEventListener('input', () => {
            if (parseInt(maxRange.value) <= parseInt(minRange.value)) {
                maxRange.value = parseInt(minRange.value) + 1;
            }
            updatePriceDisplays();
            clearActivePreset();
        });

        // Preset button handlers
        presetButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();

                const [min, max] = button.getAttribute('data-price-preset').split('-');

                minRange.value = min;
                maxRange.value = parseInt(max) > rangeMaxValue ? rangeMaxValue : max;

                updatePriceDisplays();

                // Toggle active state
                clearActivePreset();
                button.classList.add('active');

                // Trigger BOTH sliders to update filters via change events
                // data-filter-input listens for 'change', not 'input'
                minRange.dispatchEvent(new Event('change', { bubbles: true }));
                maxRange.dispatchEvent(new Event('change', { bubbles: true }));

                // Close dropdown if button has data-close-on-click
                if (button.hasAttribute('data-close-on-click')) {
                    // Find and close the nearest dropdown
                    const dropdown = button.closest('[data-filter-dropdown]');
                    if (dropdown) {
                        dropdown.classList.remove('is-active');
                    }
                }
            });
        });

        // Initialize on load - update displays, bar position, and active states
        updatePriceDisplays();
        updateSliderRange();
        updateActivePreset();
    }

    /**
     * Debounce utility
     */
    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }
}

// Register the custom element
customElements.define('collection-filter', CollectionFilter);
